/**
 * @fileoverview OptimizationTracker service
 * Handles SSE and polling-based progress tracking for optimizations
 * Extracted from EnhancedBacktestService for separation of concerns
 */

import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';
import type { OptimizationProgress, BacktestStats } from '../../types/store';
import { optimizationEventService } from './OptimizationEventService';
import type { OptimizationEvent } from './OptimizationEventService';

export interface TrackingOptions {
  useSSE?: boolean; // Use Server-Sent Events instead of polling
  fallbackToPoll?: boolean; // Fallback to polling if SSE fails
  timeout?: number; // Overall timeout in milliseconds
  pollInterval?: number; // Polling interval in milliseconds
}

interface ServerProgressResponse {
  current?: number;
  total?: number;
  progress?: number;
  completed?: boolean;
  bestSoFar?: {
    parameters: Record<string, number>;
    score: number;
    metrics: BacktestStats;
  };
  error?: string;
  // Completed job data (available when polling a finished optimization)
  status?: string;
  result?: {
    bestResult?: {
      parameters: Record<string, number>;
      score: number;
      metrics: BacktestStats;
    };
    allResults?: Array<{
      parameters: Record<string, number>;
      metrics: Record<string, number>;
      score: number;
    }>;
    optimizationTarget?: string;
    parameterRanges?: Record<string, { min: number; max: number; step: number }>;
  };
}

export class OptimizationTracker {
  /**
   * Track optimization progress with SSE or polling
   */
  async trackProgress(
    optimizationId: string,
    onProgress: (progress: OptimizationProgress) => void,
    options: TrackingOptions = {}
  ): Promise<OptimizationProgress> {
    const {
      useSSE = true,
      fallbackToPoll = true,
      timeout = 300000
    } = options;

    const sseConnected = optimizationEventService.isConnectedToSSE();

    if (useSSE && sseConnected) {
      try {
        return await this.trackWithSSE(optimizationId, onProgress, timeout);
      } catch (error) {
        if (fallbackToPoll) {
          return await this.trackWithPolling(optimizationId, onProgress, timeout, options.pollInterval);
        }
        throw error;
      }
    } else {
      return await this.trackWithPolling(optimizationId, onProgress, timeout, options.pollInterval);
    }
  }

  /**
   * Track optimization progress using Server-Sent Events
   */
  private async trackWithSSE(
    optimizationId: string,
    onProgress: (progress: OptimizationProgress) => void,
    timeout: number
  ): Promise<OptimizationProgress> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Optimization timeout'));
      }, timeout);

      const unsubscribe = optimizationEventService.subscribe(optimizationId, (event: OptimizationEvent) => {
        try {
          if (event.type === 'progress' && 'current' in event.data) {
            const progressData = event.data as import('./OptimizationEventService').OptimizationProgress;
            const progress: OptimizationProgress = {
              current: progressData.current,
              total: progressData.total,
              progress: progressData.progress,
              bestResult: progressData.bestSoFar ? {
                parameters: progressData.bestSoFar.parameters,
                score: progressData.bestSoFar.score,
                metrics: progressData.bestSoFar.metrics as BacktestStats
              } : undefined,
              totalCombinations: progressData.total,
              isComplete: false
            };
            onProgress(progress);
          } else if (event.type === 'completed') {
            clearTimeout(timeoutId);
            unsubscribe();
            
            // The completed event contains the full optimization result structure
            const completedData = event.data as { 
              bestResult?: { parameters: Record<string, number>; score: number; metrics: BacktestStats };
              parameters?: Record<string, number>; 
              score?: number; 
              metrics?: BacktestStats; 
              total?: number;
              totalCombinations?: number;
              allResults?: Array<{ parameters: Record<string, number>; metrics: Record<string, number>; score: number }>;
              optimizationTarget?: string;
            };
            
            // Handle both new format (bestResult) and old format (parameters directly)
            const bestResult = completedData.bestResult || (completedData.parameters ? {
              parameters: completedData.parameters,
              score: completedData.score || 0,
              metrics: completedData.metrics as BacktestStats
            } : undefined);
            
            const total = completedData.totalCombinations || completedData.total || 1;
            
            const finalResult: OptimizationProgress = {
              current: total,
              total: total,
              progress: 100,
              bestResult: bestResult,
              totalCombinations: total,
              isComplete: true,
              allResults: completedData.allResults,
              optimizationTarget: completedData.optimizationTarget
            };
            
            onProgress(finalResult);
            resolve(finalResult);
          } else if (event.type === 'failed' && 'error' in event.data) {
            clearTimeout(timeoutId);
            unsubscribe();
            const errorData = event.data as { error: string };
            reject(new Error(errorData.error || 'Optimization failed'));
          }
        } catch (error) {
        }
      });
    });
  }

  /**
   * Track optimization progress using traditional polling
   */
  private async trackWithPolling(
    optimizationId: string,
    onProgress: (progress: OptimizationProgress) => void,
    timeout: number,
    pollInterval: number = 5000
  ): Promise<OptimizationProgress> {
    const startTime = Date.now();
    
    const poll = async (): Promise<OptimizationProgress> => {
      if (Date.now() - startTime > timeout) {
        throw new Error('Optimization timeout');
      }

      try {
        const response = await apiClient.get<{
          success: boolean;
          data?: ServerProgressResponse;  // Server returns "data", not "progress"
          progress?: ServerProgressResponse;  // Keep for backwards compatibility
        }>(API_ENDPOINTS.OPTIMIZATION.PROGRESS(optimizationId));

        if (!response.success) {
          throw new Error('Failed to get optimization progress');
        }

        // Handle both "data" (correct) and "progress" (legacy) response formats
        const serverProgress = response.data || response.progress;
        
        if (!serverProgress) {
          throw new Error('No progress data in response');
        }
        
        // Check if the job is completed and has result data
        const isComplete = serverProgress.completed || serverProgress.status === 'completed';
        const resultData = serverProgress.result;
        
        // Extract bestResult - prefer result.bestResult for completed jobs
        const bestResult = resultData?.bestResult || (serverProgress.bestSoFar ? {
          parameters: serverProgress.bestSoFar.parameters || {},
          score: serverProgress.bestSoFar.score || 0,
          metrics: serverProgress.bestSoFar.metrics
        } : undefined);
        
        const progress: OptimizationProgress = {
          current: serverProgress.current || 0,
          total: serverProgress.total || 1,
          progress: serverProgress.progress || 0,
          bestResult,
          totalCombinations: serverProgress.total || 1,
          isComplete,
          // Include allResults and other optimization data when available
          allResults: resultData?.allResults,
          optimizationTarget: resultData?.optimizationTarget,
          parameterRanges: resultData?.parameterRanges
        };

        onProgress(progress);

        if (progress.isComplete) {
          return progress;
        }

        if (serverProgress.error) {
          throw new Error(serverProgress.error);
        }

        // Continue polling
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        return poll();

      } catch (error) {
        throw error instanceof Error ? error : new Error('Unknown error during optimization polling');
      }
    };

    return poll();
  }
}

// Export singleton instance
export const optimizationTracker = new OptimizationTracker();
